import { useState, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, FormHelperText, FormLabel, Autocomplete } from '@mui/joy';
import { useSearchIngredientsQuery } from '../api';
import { useDebounce } from '@/hooks/useDebounce';

export function IngredientAutocomplete({
  name,
  label,
  placeholder,
  optional,
  onBlur,
  onSelect,
  onSelectOption,
  sx
}) {
  const { control, getFieldState, watch } = useFormContext();
  const error = getFieldState(name).error;
  const fieldValue = watch(name);
  const [inputValue, setInputValue] = useState(fieldValue || '');
  const debouncedSearchTerm = useDebounce(inputValue, 200);

  useEffect(() => {
    if (fieldValue !== inputValue) {
      setInputValue(fieldValue || '');
    }
  }, [fieldValue]);
  
  const { data, isLoading } = useSearchIngredientsQuery({ name: debouncedSearchTerm });

  const options = data?.data || [];

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl error={!!error} sx={sx}>
          {label && (
            <FormLabel>
              {label} {optional && <span style={{ color: 'var(--joy-palette-neutral-400)' }}>(optional)</span>}
            </FormLabel>
          )}
          <Autocomplete
            placeholder={placeholder}
            options={options}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
            value={field.value || null}
            onChange={(event, newValue) => {
              const value = newValue ? (typeof newValue === 'string' ? newValue : newValue.name) : '';
              field.onChange(value);
              setInputValue(value);
              if (onSelect) {
                onSelect(value);
              }
              if (onSelectOption) {
                onSelectOption(newValue && typeof newValue !== 'string' ? newValue : null);
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              setInputValue(newInputValue);
              if (reason === 'input') {
                field.onChange(newInputValue);
              }              
            }}            
            onBlur={() => onBlur(field.value)}
            inputValue={inputValue}
            loading={isLoading}
            freeSolo            
            sx={{ width: '100%' }}
          />
          {error && (
            <FormHelperText sx={{ ml: 0 }}>{error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
